// Turn an ISO date / datetime string into a human-readable label, formatted in UTC so a
// midnight-UTC date (e.g. "2026-07-13T00:00:00.000Z") doesn't shift a day in the local timezone.
// Returns null when the input isn't an ISO date string, so callers can fall through to their
// default rendering.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export function formatIsoDate(value: unknown): string | null {
  if (typeof value !== 'string' || !ISO_DATE_RE.test(value)) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  // Include the time only when it isn't midnight UTC (i.e. a real timestamp, not a plain date).
  const hasTime = d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0;
  if (!hasTime) return date;
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  return `${date}, ${time}`;
}

export type DatePrecision = 'DAY' | 'MONTH' | 'YEAR';

// Date-only label for case dates, honouring the schema's precision flag: YEAR → "2025",
// MONTH → "March 2025", DAY / unset → "4 March 2025". Always formatted in UTC so a plain
// midnight-UTC date never shifts a day, and never locale-dependent so an international team
// reads one unambiguous form. Returns '' for a missing or unparseable value so callers can
// render it inline without a null check.
export function formatCaseDate(value: unknown, precision?: DatePrecision | string | null): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(String(value));
  if (isNaN(d.getTime())) return '';
  if (precision === 'YEAR') return String(d.getUTCFullYear());
  if (precision === 'MONTH') return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}
